import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import AdminLogin from './components/AdminLogin';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import MainLayout from './components/MainLayout/MainLayout';
import CategoryDiscountManagement from './components/CategoryDiscountManagement';
import UserManagement from './components/UserManagement';
import BannerManagement from './components/BannerManagement';
import OrderManagement from './components/OrderManagement';
import OrderDetailsPage from './pages/OrderDetailsPage';
import NotificationManagement from './components/NotificationManagement';
import ChatDashboard from './components/ChatDashboard';
import BillToBillManagement from './components/BillToBillManagement';
import TenureManagement from './components/TenureManagement';
import WalletTopupRequests from './pages/WalletTopupRequests';
import PointsRulesManagement from './components/PointsRulesManagement';
import PointsAnalytics from './components/PointsAnalytics';
import FloatingChatNotification from './components/FloatingChatNotification';
import ToastProvider from './components/ToastProvider';
import AuthTest from './pages/AuthTest';
import ModernDesignExample from './components/ModernDesignExample';
import './App.css';

/**
 * AppContent Component
 * Handles routing and navigation logic with access to router hooks
 */
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const authContext = React.useContext(AuthContext);

  // Initialize auth context in service
  React.useEffect(() => {
    const { setAuthContext } = require('./services/api');
    setAuthContext(authContext);
  }, [authContext]);

  // Handler for menu navigation
  const handleNavigate = (path) => {
    navigate(path);
  };

  // Check if current route should show the layout
  const isLoginPage = location.pathname === '/login';
  const isSupportPage = location.pathname === '/support';

  return (
    <div className="App">
      {isLoginPage ? (
        // Login page without layout
        <Routes>
          <Route path="/login" element={<AdminLogin />} />
        </Routes>
      ) : (
        // All other routes wrapped with MainLayout
        <MainLayout
          currentPath={location.pathname + location.search}
          onNavigate={handleNavigate}
        >
          <Routes>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discounts"
              element={
                <ProtectedRoute>
                  <CategoryDiscountManagement />
                </ProtectedRoute>
              }
            />
            {/* Catalogue masters are managed exclusively in Inventory. */}
            <Route path="/products" element={<Navigate to="/dashboard" replace />} />
            <Route path="/categories" element={<Navigate to="/dashboard" replace />} />
            <Route path="/subcategories" element={<Navigate to="/dashboard" replace />} />
            <Route path="/brands" element={<Navigate to="/dashboard" replace />} />
            <Route path="/pricing" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bill-to-bill"
              element={
                <ProtectedRoute>
                  <BillToBillManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenure"
              element={
                <ProtectedRoute>
                  <TenureManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wallet-topup"
              element={
                <ProtectedRoute>
                  <WalletTopupRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/points-rules"
              element={
                <ProtectedRoute>
                  <PointsRulesManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/points-analytics"
              element={
                <ProtectedRoute>
                  <PointsAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/banners"
              element={
                <ProtectedRoute>
                  <BannerManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <OrderManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:orderId"
              element={
                <ProtectedRoute>
                  <OrderDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <ChatDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/auth-test" element={<AuthTest />} />
            <Route
              path="/design-example"
              element={
                <ProtectedRoute>
                  <ModernDesignExample />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </MainLayout>
      )}

      {/* Floating notification is redundant inside the full Support workspace. */}
      {!isLoginPage && !isSupportPage && <FloatingChatNotification />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider />
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
